import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FileText, Scale, ShieldCheck, Truck, CreditCard, AlertTriangle, RefreshCw, Mail } from 'lucide-react';
import { FlagImg } from '../components/FlagImg';

export const GeneralTerms = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'General Terms and Conditions of Sale',
      subtitle: 'Terms governing commercial transactions',
      lastUpdated: 'Last updated: February 2025',
      intro: {
        title: 'Scope of Application',
        text: 'These General Terms and Conditions of Sale ("Terms") govern all commercial transactions between TRH Steel and its customers for the supply of steel products and related services. By placing an order, the customer accepts these Terms in their entirety.',
        text2: 'These Terms apply to transactions with either of the following TRH Steel entities, depending on the point of sale:'
      },
      entities: {
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, Spain'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'VAT: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Liège, Belgium'
        }
      },
      sections: {
        orders: {
          title: 'Orders and Order Confirmation',
          items: [
            'All orders must be placed in writing (email, fax, or online order system) and are subject to acceptance by TRH Steel.',
            'An order is only binding upon written confirmation from TRH Steel, which will specify quantities, prices, delivery terms, and payment conditions.',
            'Any modification to a confirmed order requires written agreement from both parties.',
            'TRH Steel reserves the right to refuse or cancel any order for legitimate business reasons.'
          ]
        },
        prices: {
          title: 'Prices and Payment Terms',
          items: [
            'Prices are quoted ex-works unless otherwise specified and are exclusive of VAT and other applicable taxes.',
            'Prices are valid for the period stated in the quotation. Beyond this period, TRH Steel reserves the right to revise prices.',
            'Payment terms are as specified in the invoice. Standard payment term is 30 days from invoice date.',
            'Late payments will incur interest at the legal rate plus administrative costs.',
            'TRH Steel reserves the right to require advance payment or bank guarantees for new customers or large orders.'
          ]
        },
        delivery: {
          title: 'Delivery and Risk Transfer',
          items: [
            'Delivery dates are indicative and not binding. TRH Steel will make reasonable efforts to meet agreed delivery schedules.',
            'Unless otherwise agreed, delivery is ex-works (EXW Incoterms 2020).',
            'Risk of loss or damage transfers to the customer upon delivery to the carrier or, in the case of collection, when the goods are made available.',
            'Partial deliveries are permitted unless expressly excluded in the order confirmation.',
            'The customer must inspect goods upon receipt and report any visible defects within 48 hours.'
          ]
        },
        quality: {
          title: 'Quality and Specifications',
          items: [
            'Products supplied conform to the specifications stated in the order confirmation and relevant EN/ISO standards.',
            'Mill test certificates are provided upon request for certified products.',
            'Dimensional tolerances are in accordance with applicable European standards unless otherwise agreed.',
            'TRH Steel\'s quality management system is certified to ISO 9001.'
          ]
        },
        warranty: {
          title: 'Warranty and Liability',
          items: [
            'TRH Steel warrants that products are free from material defects at the time of delivery.',
            'Claims for hidden defects must be notified in writing within 8 days of discovery and no later than 12 months from delivery.',
            'Warranty is limited to replacement of defective products or credit. Consequential damages are excluded to the extent permitted by law.',
            'Warranty does not cover defects resulting from improper storage, handling, or use by the customer.'
          ]
        },
        retention: {
          title: 'Retention of Title',
          items: [
            'TRH Steel retains ownership of all goods until full payment has been received.',
            'Until ownership transfers, the customer must store goods separately and maintain them in good condition.',
            'In case of non-payment, TRH Steel may reclaim the goods without prejudice to other legal remedies.'
          ]
        },
        force: {
          title: 'Force Majeure',
          text: 'Neither party shall be liable for failure to perform its obligations due to circumstances beyond its reasonable control, including but not limited to: natural disasters, war, strikes, government actions, supply chain disruptions, or energy shortages. The affected party must notify the other party promptly and both parties shall work together to mitigate the impact.'
        },
        disputes: {
          title: 'Applicable Law and Dispute Resolution',
          spain: 'For transactions with TRH ZF Sevilla: Spanish law applies. Disputes shall be resolved by the courts of Seville, Spain.',
          belgium: 'For transactions with TRH Liège: Belgian law applies. Disputes shall be resolved by the courts of Liège, Belgium.',
          eu: 'For cross-border B2B disputes within the EU, parties may agree to arbitration under ICC rules.'
        },
        amendments: {
          title: 'Amendments',
          text: 'TRH Steel reserves the right to modify these Terms. The applicable version is that in force at the time of order confirmation. Significant changes will be communicated to customers.'
        }
      },
      contact: {
        title: 'Contact',
        text: 'For questions regarding these Terms and Conditions, please contact:',
        email: 'legal@trhsteel.com'
      }
    },
    es: {
      title: 'Condiciones Generales de Venta',
      subtitle: 'Términos que rigen las transacciones comerciales',
      lastUpdated: 'Última actualización: Febrero 2025',
      intro: {
        title: 'Ámbito de Aplicación',
        text: 'Estas Condiciones Generales de Venta ("Condiciones") rigen todas las transacciones comerciales entre TRH Steel y sus clientes para el suministro de productos de acero y servicios relacionados. Al realizar un pedido, el cliente acepta estas Condiciones en su totalidad.',
        text2: 'Estas Condiciones se aplican a las transacciones con cualquiera de las siguientes entidades de TRH Steel, según el punto de venta:'
      },
      entities: {
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, España'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'IVA: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Lieja, Bélgica'
        }
      },
      sections: {
        orders: {
          title: 'Pedidos y Confirmación de Pedido',
          items: [
            'Todos los pedidos deben realizarse por escrito (email, fax o sistema de pedidos online) y están sujetos a aceptación por parte de TRH Steel.',
            'Un pedido solo es vinculante tras la confirmación escrita de TRH Steel, que especificará cantidades, precios, condiciones de entrega y condiciones de pago.',
            'Cualquier modificación de un pedido confirmado requiere acuerdo escrito de ambas partes.',
            'TRH Steel se reserva el derecho de rechazar o cancelar cualquier pedido por razones comerciales legítimas.'
          ]
        },
        prices: {
          title: 'Precios y Condiciones de Pago',
          items: [
            'Los precios se cotizan en fábrica salvo que se especifique lo contrario y no incluyen IVA ni otros impuestos aplicables.',
            'Los precios son válidos durante el período indicado en la oferta. Más allá de este período, TRH Steel se reserva el derecho de revisar los precios.',
            'Las condiciones de pago son las especificadas en la factura. El plazo de pago estándar es de 30 días desde la fecha de factura.',
            'Los pagos atrasados generarán intereses al tipo legal más costes administrativos.',
            'TRH Steel se reserva el derecho de exigir pago anticipado o garantías bancarias para nuevos clientes o pedidos grandes.'
          ]
        },
        delivery: {
          title: 'Entrega y Transferencia de Riesgo',
          items: [
            'Las fechas de entrega son indicativas y no vinculantes. TRH Steel hará esfuerzos razonables para cumplir los plazos de entrega acordados.',
            'Salvo acuerdo en contrario, la entrega es en fábrica (EXW Incoterms 2020).',
            'El riesgo de pérdida o daño se transfiere al cliente en el momento de la entrega al transportista o, en caso de recogida, cuando la mercancía está disponible.',
            'Las entregas parciales están permitidas a menos que se excluyan expresamente en la confirmación del pedido.',
            'El cliente debe inspeccionar la mercancía a su recepción y notificar cualquier defecto visible en un plazo de 48 horas.'
          ]
        },
        quality: {
          title: 'Calidad y Especificaciones',
          items: [
            'Los productos suministrados cumplen con las especificaciones indicadas en la confirmación del pedido y las normas EN/ISO pertinentes.',
            'Los certificados de ensayo de fábrica se proporcionan a petición para productos certificados.',
            'Las tolerancias dimensionales son conformes a las normas europeas aplicables salvo acuerdo en contrario.',
            'El sistema de gestión de calidad de TRH Steel está certificado según ISO 9001.'
          ]
        },
        warranty: {
          title: 'Garantía y Responsabilidad',
          items: [
            'TRH Steel garantiza que los productos están libres de defectos materiales en el momento de la entrega.',
            'Las reclamaciones por defectos ocultos deben notificarse por escrito en un plazo de 8 días desde su descubrimiento y no más tarde de 12 meses desde la entrega.',
            'La garantía se limita a la sustitución de los productos defectuosos o abono. Los daños consecuenciales quedan excluidos en la medida permitida por la ley.',
            'La garantía no cubre defectos resultantes del almacenamiento, manipulación o uso inadecuado por parte del cliente.'
          ]
        },
        retention: {
          title: 'Reserva de Dominio',
          items: [
            'TRH Steel retiene la propiedad de todas las mercancías hasta que se haya recibido el pago completo.',
            'Hasta que se transfiera la propiedad, el cliente debe almacenar las mercancías por separado y mantenerlas en buen estado.',
            'En caso de impago, TRH Steel puede reclamar las mercancías sin perjuicio de otros recursos legales.'
          ]
        },
        force: {
          title: 'Fuerza Mayor',
          text: 'Ninguna de las partes será responsable por el incumplimiento de sus obligaciones debido a circunstancias fuera de su control razonable, incluyendo pero no limitado a: desastres naturales, guerra, huelgas, acciones gubernamentales, interrupciones de la cadena de suministro o escasez de energía. La parte afectada debe notificar a la otra parte con prontitud y ambas partes trabajarán juntas para mitigar el impacto.'
        },
        disputes: {
          title: 'Ley Aplicable y Resolución de Disputas',
          spain: 'Para transacciones con TRH ZF Sevilla: se aplica la ley española. Las disputas serán resueltas por los tribunales de Sevilla, España.',
          belgium: 'Para transacciones con TRH Liège: se aplica la ley belga. Las disputas serán resueltas por los tribunales de Lieja, Bélgica.',
          eu: 'Para disputas B2B transfronterizas dentro de la UE, las partes pueden acordar arbitraje bajo las reglas de la CCI.'
        },
        amendments: {
          title: 'Modificaciones',
          text: 'TRH Steel se reserva el derecho de modificar estas Condiciones. La versión aplicable es la vigente en el momento de la confirmación del pedido. Los cambios significativos se comunicarán a los clientes.'
        }
      },
      contact: {
        title: 'Contacto',
        text: 'Para preguntas sobre estas Condiciones Generales, por favor contacte:',
        email: 'legal@trhsteel.com'
      }
    },
    fr: {
      title: 'Conditions Générales de Vente',
      subtitle: 'Conditions régissant les transactions commerciales',
      lastUpdated: 'Dernière mise à jour: Février 2025',
      intro: {
        title: 'Champ d\'Application',
        text: 'Ces Conditions Générales de Vente ("Conditions") régissent toutes les transactions commerciales entre TRH Steel et ses clients pour la fourniture de produits en acier et services associés. En passant commande, le client accepte ces Conditions dans leur intégralité.',
        text2: 'Ces Conditions s\'appliquent aux transactions avec l\'une des entités TRH Steel suivantes, selon le point de vente:'
      },
      entities: {
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Séville, Espagne'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'TVA: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Liège, Belgique'
        }
      },
      sections: {
        orders: {
          title: 'Commandes et Confirmation de Commande',
          items: [
            'Toutes les commandes doivent être passées par écrit (email, fax ou système de commande en ligne) et sont soumises à acceptation par TRH Steel.',
            'Une commande n\'est contraignante qu\'après confirmation écrite de TRH Steel, qui précisera les quantités, prix, conditions de livraison et conditions de paiement.',
            'Toute modification d\'une commande confirmée nécessite l\'accord écrit des deux parties.',
            'TRH Steel se réserve le droit de refuser ou d\'annuler toute commande pour des raisons commerciales légitimes.'
          ]
        },
        prices: {
          title: 'Prix et Conditions de Paiement',
          items: [
            'Les prix sont cotés départ usine sauf indication contraire et sont hors TVA et autres taxes applicables.',
            'Les prix sont valables pendant la période indiquée dans l\'offre. Au-delà de cette période, TRH Steel se réserve le droit de réviser les prix.',
            'Les conditions de paiement sont celles spécifiées sur la facture. Le délai de paiement standard est de 30 jours à compter de la date de facture.',
            'Les retards de paiement entraîneront des intérêts au taux légal plus des frais administratifs.',
            'TRH Steel se réserve le droit d\'exiger un paiement anticipé ou des garanties bancaires pour les nouveaux clients ou les commandes importantes.'
          ]
        },
        delivery: {
          title: 'Livraison et Transfert de Risque',
          items: [
            'Les dates de livraison sont indicatives et non contraignantes. TRH Steel fera des efforts raisonnables pour respecter les délais de livraison convenus.',
            'Sauf accord contraire, la livraison est départ usine (EXW Incoterms 2020).',
            'Le risque de perte ou de dommage est transféré au client lors de la livraison au transporteur ou, en cas d\'enlèvement, lorsque les marchandises sont mises à disposition.',
            'Les livraisons partielles sont autorisées sauf exclusion expresse dans la confirmation de commande.',
            'Le client doit inspecter les marchandises à réception et signaler tout défaut visible dans les 48 heures.'
          ]
        },
        quality: {
          title: 'Qualité et Spécifications',
          items: [
            'Les produits fournis sont conformes aux spécifications indiquées dans la confirmation de commande et aux normes EN/ISO pertinentes.',
            'Les certificats d\'essai d\'usine sont fournis sur demande pour les produits certifiés.',
            'Les tolérances dimensionnelles sont conformes aux normes européennes applicables sauf accord contraire.',
            'Le système de management de la qualité de TRH Steel est certifié ISO 9001.'
          ]
        },
        warranty: {
          title: 'Garantie et Responsabilité',
          items: [
            'TRH Steel garantit que les produits sont exempts de défauts matériels au moment de la livraison.',
            'Les réclamations pour vices cachés doivent être notifiées par écrit dans les 8 jours suivant leur découverte et au plus tard 12 mois après la livraison.',
            'La garantie est limitée au remplacement des produits défectueux ou à un avoir. Les dommages consécutifs sont exclus dans la mesure permise par la loi.',
            'La garantie ne couvre pas les défauts résultant d\'un stockage, d\'une manipulation ou d\'une utilisation inappropriée par le client.'
          ]
        },
        retention: {
          title: 'Réserve de Propriété',
          items: [
            'TRH Steel conserve la propriété de toutes les marchandises jusqu\'au paiement intégral.',
            'Jusqu\'au transfert de propriété, le client doit stocker les marchandises séparément et les maintenir en bon état.',
            'En cas de non-paiement, TRH Steel peut réclamer les marchandises sans préjudice d\'autres recours légaux.'
          ]
        },
        force: {
          title: 'Force Majeure',
          text: 'Aucune des parties ne sera responsable de l\'inexécution de ses obligations en raison de circonstances indépendantes de sa volonté raisonnable, y compris mais sans s\'y limiter: catastrophes naturelles, guerre, grèves, actions gouvernementales, perturbations de la chaîne d\'approvisionnement ou pénuries d\'énergie. La partie concernée doit en informer rapidement l\'autre partie et les deux parties travailleront ensemble pour atténuer l\'impact.'
        },
        disputes: {
          title: 'Loi Applicable et Résolution des Litiges',
          spain: 'Pour les transactions avec TRH ZF Sevilla: le droit espagnol s\'applique. Les litiges seront résolus par les tribunaux de Séville, Espagne.',
          belgium: 'Pour les transactions avec TRH Liège: le droit belge s\'applique. Les litiges seront résolus par les tribunaux de Liège, Belgique.',
          eu: 'Pour les litiges B2B transfrontaliers au sein de l\'UE, les parties peuvent convenir d\'un arbitrage selon les règles de la CCI.'
        },
        amendments: {
          title: 'Modifications',
          text: 'TRH Steel se réserve le droit de modifier ces Conditions. La version applicable est celle en vigueur au moment de la confirmation de commande. Les changements significatifs seront communiqués aux clients.'
        }
      },
      contact: {
        title: 'Contact',
        text: 'Pour toute question concernant ces Conditions Générales, veuillez contacter:',
        email: 'legal@trhsteel.com'
      }
    },
    de: {
      title: 'Allgemeine Verkaufsbedingungen',
      subtitle: 'Bedingungen für Geschäftstransaktionen',
      lastUpdated: 'Letzte Aktualisierung: Februar 2025',
      intro: {
        title: 'Geltungsbereich',
        text: 'Diese Allgemeinen Verkaufsbedingungen ("Bedingungen") regeln alle Geschäftstransaktionen zwischen TRH Steel und seinen Kunden für die Lieferung von Stahlprodukten und damit verbundenen Dienstleistungen. Mit der Auftragserteilung akzeptiert der Kunde diese Bedingungen in vollem Umfang.',
        text2: 'Diese Bedingungen gelten für Transaktionen mit einer der folgenden TRH Steel-Gesellschaften, je nach Verkaufsort:'
      },
      entities: {
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, Spanien'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'USt-IdNr.: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Lüttich, Belgien'
        }
      },
      sections: {
        orders: {
          title: 'Bestellungen und Auftragsbestätigung',
          items: [
            'Alle Bestellungen müssen schriftlich erfolgen (E-Mail, Fax oder Online-Bestellsystem) und unterliegen der Annahme durch TRH Steel.',
            'Eine Bestellung ist erst nach schriftlicher Bestätigung durch TRH Steel verbindlich, die Mengen, Preise, Lieferbedingungen und Zahlungsbedingungen festlegt.',
            'Jede Änderung einer bestätigten Bestellung erfordert die schriftliche Zustimmung beider Parteien.',
            'TRH Steel behält sich das Recht vor, Bestellungen aus legitimen geschäftlichen Gründen abzulehnen oder zu stornieren.'
          ]
        },
        prices: {
          title: 'Preise und Zahlungsbedingungen',
          items: [
            'Die Preise verstehen sich ab Werk, sofern nicht anders angegeben, und sind exklusive Mehrwertsteuer und anderer anfallender Steuern.',
            'Die Preise gelten für den im Angebot genannten Zeitraum. Nach Ablauf dieses Zeitraums behält sich TRH Steel das Recht vor, die Preise anzupassen.',
            'Die Zahlungsbedingungen sind wie in der Rechnung angegeben. Standardzahlungsziel ist 30 Tage ab Rechnungsdatum.',
            'Verspätete Zahlungen werden mit dem gesetzlichen Zinssatz zuzüglich Verwaltungskosten belastet.',
            'TRH Steel behält sich das Recht vor, bei Neukunden oder größeren Bestellungen Vorauszahlung oder Bankgarantien zu verlangen.'
          ]
        },
        delivery: {
          title: 'Lieferung und Risikoübergang',
          items: [
            'Liefertermine sind unverbindlich. TRH Steel wird angemessene Anstrengungen unternehmen, um vereinbarte Liefertermine einzuhalten.',
            'Sofern nicht anders vereinbart, erfolgt die Lieferung ab Werk (EXW Incoterms 2020).',
            'Das Risiko von Verlust oder Beschädigung geht bei Übergabe an den Spediteur auf den Kunden über, bei Selbstabholung bei Bereitstellung der Ware.',
            'Teillieferungen sind zulässig, sofern nicht ausdrücklich in der Auftragsbestätigung ausgeschlossen.',
            'Der Kunde muss die Ware bei Erhalt prüfen und sichtbare Mängel innerhalb von 48 Stunden melden.'
          ]
        },
        quality: {
          title: 'Qualität und Spezifikationen',
          items: [
            'Die gelieferten Produkte entsprechen den in der Auftragsbestätigung genannten Spezifikationen und den relevanten EN/ISO-Normen.',
            'Werksabnahmezeugnisse werden auf Anfrage für zertifizierte Produkte bereitgestellt.',
            'Maßtoleranzen entsprechen den geltenden europäischen Normen, sofern nicht anders vereinbart.',
            'Das Qualitätsmanagementsystem von TRH Steel ist nach ISO 9001 zertifiziert.'
          ]
        },
        warranty: {
          title: 'Gewährleistung und Haftung',
          items: [
            'TRH Steel gewährleistet, dass die Produkte zum Zeitpunkt der Lieferung frei von Materialfehlern sind.',
            'Ansprüche wegen verdeckter Mängel müssen innerhalb von 8 Tagen nach Entdeckung und spätestens 12 Monate nach Lieferung schriftlich geltend gemacht werden.',
            'Die Gewährleistung ist auf den Ersatz mangelhafter Produkte oder Gutschrift beschränkt. Folgeschäden sind im gesetzlich zulässigen Umfang ausgeschlossen.',
            'Die Gewährleistung deckt keine Mängel ab, die auf unsachgemäße Lagerung, Handhabung oder Verwendung durch den Kunden zurückzuführen sind.'
          ]
        },
        retention: {
          title: 'Eigentumsvorbehalt',
          items: [
            'TRH Steel behält das Eigentum an allen Waren bis zum vollständigen Zahlungseingang.',
            'Bis zur Eigentumsübertragung muss der Kunde die Waren getrennt lagern und in gutem Zustand halten.',
            'Bei Nichtzahlung kann TRH Steel die Waren zurückfordern, unbeschadet anderer Rechtsbehelfe.'
          ]
        },
        force: {
          title: 'Höhere Gewalt',
          text: 'Keine Partei haftet für die Nichterfüllung ihrer Verpflichtungen aufgrund von Umständen außerhalb ihrer angemessenen Kontrolle, einschließlich, aber nicht beschränkt auf: Naturkatastrophen, Krieg, Streiks, behördliche Maßnahmen, Unterbrechungen der Lieferkette oder Energieknappheit. Die betroffene Partei muss die andere Partei unverzüglich benachrichtigen und beide Parteien werden zusammenarbeiten, um die Auswirkungen zu mindern.'
        },
        disputes: {
          title: 'Anwendbares Recht und Streitbeilegung',
          spain: 'Für Transaktionen mit TRH ZF Sevilla: Es gilt spanisches Recht. Streitigkeiten werden von den Gerichten in Sevilla, Spanien, entschieden.',
          belgium: 'Für Transaktionen mit TRH Liège: Es gilt belgisches Recht. Streitigkeiten werden von den Gerichten in Lüttich, Belgien, entschieden.',
          eu: 'Für grenzüberschreitende B2B-Streitigkeiten innerhalb der EU können die Parteien ein Schiedsverfahren nach ICC-Regeln vereinbaren.'
        },
        amendments: {
          title: 'Änderungen',
          text: 'TRH Steel behält sich das Recht vor, diese Bedingungen zu ändern. Die anwendbare Fassung ist diejenige, die zum Zeitpunkt der Auftragsbestätigung gilt. Wesentliche Änderungen werden den Kunden mitgeteilt.'
        }
      },
      contact: {
        title: 'Kontakt',
        text: 'Bei Fragen zu diesen Allgemeinen Geschäftsbedingungen wenden Sie sich bitte an:',
        email: 'legal@trhsteel.com'
      }
    },
    nl: {
      title: 'Algemene Verkoopvoorwaarden',
      subtitle: 'Voorwaarden voor commerciële transacties',
      lastUpdated: 'Laatst bijgewerkt: Februari 2025',
      intro: {
        title: 'Toepassingsgebied',
        text: 'Deze Algemene Verkoopvoorwaarden ("Voorwaarden") zijn van toepassing op alle commerciële transacties tussen TRH Steel en haar klanten voor de levering van staalproducten en aanverwante diensten. Door een bestelling te plaatsen accepteert de klant deze Voorwaarden in hun geheel.',
        text2: 'Deze Voorwaarden zijn van toepassing op transacties met een van de volgende TRH Steel-entiteiten, afhankelijk van het verkooppunt:'
      },
      entities: {
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, Spanje'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'BTW: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Luik, België'
        }
      },
      sections: {
        orders: {
          title: 'Bestellingen en Orderbevestiging',
          items: [
            'Alle bestellingen moeten schriftelijk worden geplaatst (e-mail, fax of online bestelsysteem) en zijn onderhevig aan acceptatie door TRH Steel.',
            'Een bestelling is pas bindend na schriftelijke bevestiging door TRH Steel, waarin hoeveelheden, prijzen, leveringsvoorwaarden en betalingsvoorwaarden worden gespecificeerd.',
            'Elke wijziging van een bevestigde bestelling vereist schriftelijke overeenstemming van beide partijen.',
            'TRH Steel behoudt zich het recht voor om elke bestelling te weigeren of te annuleren om legitieme zakelijke redenen.'
          ]
        },
        prices: {
          title: 'Prijzen en Betalingsvoorwaarden',
          items: [
            'Prijzen zijn af fabriek tenzij anders vermeld en zijn exclusief BTW en andere toepasselijke belastingen.',
            'Prijzen zijn geldig voor de in de offerte vermelde periode. Na deze periode behoudt TRH Steel zich het recht voor de prijzen te herzien.',
            'De betalingsvoorwaarden zijn zoals vermeld op de factuur. De standaard betalingstermijn is 30 dagen na factuurdatum.',
            'Te late betalingen worden belast met wettelijke rente plus administratiekosten.',
            'TRH Steel behoudt zich het recht voor om vooruitbetaling of bankgaranties te eisen voor nieuwe klanten of grote bestellingen.'
          ]
        },
        delivery: {
          title: 'Levering en Risico-overgang',
          items: [
            'Leveringsdata zijn indicatief en niet bindend. TRH Steel zal redelijke inspanningen leveren om afgesproken leveringsschema\'s te halen.',
            'Tenzij anders overeengekomen, is de levering af fabriek (EXW Incoterms 2020).',
            'Het risico van verlies of schade gaat over op de klant bij levering aan de vervoerder of, in geval van afhaling, wanneer de goederen beschikbaar worden gesteld.',
            'Deelleveringen zijn toegestaan tenzij uitdrukkelijk uitgesloten in de orderbevestiging.',
            'De klant moet de goederen bij ontvangst inspecteren en zichtbare gebreken binnen 48 uur melden.'
          ]
        },
        quality: {
          title: 'Kwaliteit en Specificaties',
          items: [
            'Geleverde producten voldoen aan de specificaties vermeld in de orderbevestiging en relevante EN/ISO-normen.',
            'Fabriekscertificaten worden op verzoek verstrekt voor gecertificeerde producten.',
            'Dimensionale toleranties zijn in overeenstemming met toepasselijke Europese normen tenzij anders overeengekomen.',
            'Het kwaliteitsmanagementsysteem van TRH Steel is gecertificeerd volgens ISO 9001.'
          ]
        },
        warranty: {
          title: 'Garantie en Aansprakelijkheid',
          items: [
            'TRH Steel garandeert dat producten vrij zijn van materiaaldefecten op het moment van levering.',
            'Claims voor verborgen gebreken moeten schriftelijk worden gemeld binnen 8 dagen na ontdekking en uiterlijk 12 maanden na levering.',
            'Garantie is beperkt tot vervanging van defecte producten of creditering. Gevolgschade is uitgesloten voor zover wettelijk toegestaan.',
            'Garantie dekt geen gebreken als gevolg van onjuiste opslag, behandeling of gebruik door de klant.'
          ]
        },
        retention: {
          title: 'Eigendomsvoorbehoud',
          items: [
            'TRH Steel behoudt het eigendom van alle goederen tot volledige betaling is ontvangen.',
            'Tot de eigendomsoverdracht moet de klant de goederen apart opslaan en in goede staat houden.',
            'Bij niet-betaling kan TRH Steel de goederen terugvorderen onverminderd andere rechtsmiddelen.'
          ]
        },
        force: {
          title: 'Overmacht',
          text: 'Geen der partijen is aansprakelijk voor het niet nakomen van haar verplichtingen als gevolg van omstandigheden buiten haar redelijke controle, met inbegrip van maar niet beperkt tot: natuurrampen, oorlog, stakingen, overheidsmaatregelen, verstoringen van de toeleveringsketen of energietekorten. De getroffen partij moet de andere partij onverwijld op de hoogte stellen en beide partijen zullen samenwerken om de impact te beperken.'
        },
        disputes: {
          title: 'Toepasselijk Recht en Geschillenbeslechting',
          spain: 'Voor transacties met TRH ZF Sevilla: Spaans recht is van toepassing. Geschillen worden beslecht door de rechtbanken van Sevilla, Spanje.',
          belgium: 'Voor transacties met TRH Liège: Belgisch recht is van toepassing. Geschillen worden beslecht door de rechtbanken van Luik, België.',
          eu: 'Voor grensoverschrijdende B2B-geschillen binnen de EU kunnen partijen arbitrage overeenkomen onder ICC-regels.'
        },
        amendments: {
          title: 'Wijzigingen',
          text: 'TRH Steel behoudt zich het recht voor deze Voorwaarden te wijzigen. De toepasselijke versie is die welke van kracht is op het moment van de orderbevestiging. Belangrijke wijzigingen worden aan klanten meegedeeld.'
        }
      },
      contact: {
        title: 'Contact',
        text: 'Voor vragen over deze Algemene Voorwaarden kunt u contact opnemen met:',
        email: 'legal@trhsteel.com'
      }
    }
  };

  const c = content[language] || content.en;

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="general-terms-page">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="h-8 w-8 text-green-500" />
            <span className="text-green-500 text-sm uppercase tracking-wider font-bold">B2B</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-4">
            {c.title}
          </h1>
          <p className="text-slate-400">{c.subtitle}</p>
          <p className="text-slate-500 text-sm mt-4">{c.lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 p-8 md:p-12 space-y-10">
            
            {/* Introduction */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.intro.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.intro.text}</p>
              <p className="text-slate-600 leading-relaxed mb-4">{c.intro.text2}</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 border border-slate-200">
                  <FlagImg code="ES" size={24} />
                  <p className="font-bold text-slate-900 mt-2">{c.entities.spain.name}</p>
                  <p className="text-sm text-slate-600">{c.entities.spain.id}</p>
                  <p className="text-xs text-slate-500">{c.entities.spain.address}</p>
                </div>
                <div className="bg-slate-50 p-4 border border-slate-200">
                  <FlagImg code="BE" size={24} />
                  <p className="font-bold text-slate-900 mt-2">{c.entities.belgium.name}</p>
                  <p className="text-sm text-slate-600">{c.entities.belgium.id}</p>
                  <p className="text-xs text-slate-500">{c.entities.belgium.address}</p>
                </div>
              </div>
            </div>

            {/* Orders */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-green-600" />
                {c.sections.orders.title}
              </h2>
              <ul className="space-y-2">
                {c.sections.orders.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-3 text-slate-600">
                    <span className="text-green-600 font-bold mt-0.5">{i + 1}.</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Prices */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-green-600" />
                {c.sections.prices.title}
              </h2>
              <ul className="space-y-2">
                {c.sections.prices.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-3 text-slate-600">
                    <span className="text-green-600 font-bold mt-0.5">{i + 1}.</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Delivery */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Truck className="h-5 w-5 mr-2 text-green-600" />
                {c.sections.delivery.title}
              </h2>
              <ul className="space-y-2">
                {c.sections.delivery.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-3 text-slate-600">
                    <span className="text-green-600 font-bold mt-0.5">{i + 1}.</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quality */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <ShieldCheck className="h-5 w-5 mr-2 text-green-600" />
                {c.sections.quality.title}
              </h2>
              <ul className="space-y-2">
                {c.sections.quality.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-3 text-slate-600">
                    <span className="text-green-600 font-bold mt-0.5">{i + 1}.</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Warranty */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <RefreshCw className="h-5 w-5 mr-2 text-green-600" />
                {c.sections.warranty.title}
              </h2>
              <ul className="space-y-2">
                {c.sections.warranty.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-3 text-slate-600">
                    <span className="text-green-600 font-bold mt-0.5">{i + 1}.</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Retention of Title */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.sections.retention.title}
              </h2>
              <ul className="space-y-2">
                {c.sections.retention.items.map((item, i) => (
                  <li key={i} className="flex items-start space-x-3 text-slate-600">
                    <span className="text-green-600 font-bold mt-0.5">{i + 1}.</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Force Majeure */}
            <div className="bg-amber-50 border border-amber-200 p-6">
              <h2 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight mb-2 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2 text-amber-600" />
                {c.sections.force.title}
              </h2>
              <p className="text-slate-600 text-sm">{c.sections.force.text}</p>
            </div>

            {/* Disputes */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Scale className="h-5 w-5 mr-2 text-green-600" />
                {c.sections.disputes.title}
              </h2>
              <div className="space-y-3">
                <div className="flex items-start space-x-2 p-3 bg-slate-50 border border-slate-200">
                  <FlagImg code="ES" size={20} />
                  <span className="text-slate-600 text-sm">{c.sections.disputes.spain}</span>
                </div>
                <div className="flex items-start space-x-2 p-3 bg-slate-50 border border-slate-200">
                  <FlagImg code="BE" size={20} />
                  <span className="text-slate-600 text-sm">{c.sections.disputes.belgium}</span>
                </div>
                <div className="flex items-start space-x-2 p-3 bg-slate-50 border border-slate-200">
                  <FlagImg code="EU" size={20} />
                  <span className="text-slate-600 text-sm">{c.sections.disputes.eu}</span>
                </div>
              </div>
            </div>

            {/* Amendments */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.sections.amendments.title}
              </h2>
              <p className="text-slate-600 leading-relaxed">{c.sections.amendments.text}</p>
            </div>

            {/* Contact */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-green-600" />
                {c.contact.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.contact.text}</p>
              <div className="bg-slate-900 text-white p-4">
                <a href={`mailto:${c.contact.email}`} className="text-green-400 hover:text-green-300">{c.contact.email}</a>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
